/*
 * PDP in-place product swap (TW-218).
 *
 * Colours and styles are separate products, so a colour swatch or a Style tab
 * normally loads another product page. With the flag on, the target page is
 * fetched (and prefetched on hover / focus), then #MainContent and the sticky
 * ATC bar are swapped in through the theme's HTMLUpdateUtility.viewTransition,
 * with title, head tags, window.meta and history kept in sync.
 *
 * On when <html class="pdp-swap"> or window.PDP_SWAP === true. The main-product section's
 * "Switch colour and style without a page reload" setting (on by default) sets the class for
 * everyone; with the setting off, an A/B tool can set it per visitor instead.
 * ?pdp_swap=1 / ?pdp_swap=0 overrides either for the session.
 * Any failure falls back to a normal page load.
 */
window.PdpSwap =
  window.PdpSwap ||
  (() => {
    const FLAG = 'pdp_swap';
    const PREFETCH_DELAY = 100; // ms of hover / focus before a prefetch starts
    const PREFETCH_MAX = 10; // network prefetches per page lifetime
    const CACHE_MAX = 20; // cached pages, html strings
    const PRELOAD_IMAGES = 4; // gallery images decoded before the swap lands
    const PRELOAD_TIMEOUT = 1200; // ms; slower images pop in after the swap instead
    const HEIGHT_HOLD = 1200; // ms the swapped page keeps the old height while apps fill in
    const ALIGN_RETRIES = [60, 200, 500, 900, 1400, 2000]; // ms; late-loading widgets keep shifting the layout
    const QUIET_WINDOW = 2500; // ms the gallery must not scroll the page for us
    const SEED_ASSIGNMENT = /^\s*Spurit\.Preorder2\.snippet\.products\[(['"])[\w-]+\1\]\s*=\s*\{.*\};?\s*$/gm;
    const SEED_MAX_LENGTH = 200000; // characters; a product's variant list, nothing larger
    const HELD_WIDGETS = '.yotpo-widget-instance[data-yotpo-instance-id], product-recommendations';
    const HOLD_SETTLE = 300; // ms without DOM changes before a held block's height is re-checked
    const HOLD_QUIET = 2500; // ms without DOM changes after which a genuinely shorter block is let go
    const HOLD_MAX = 6000; // ms; a held block gives its space back after this whatever happens
    const INFO_COLUMN = '.product__info-container';
    const HEAD_TAGS =
      'link[rel="canonical"], meta[property^="og:"], meta[name^="twitter:"], meta[name="description"]';
    const STYLE_TAB = 'a.style-toggle__option';
    const SWATCH_INPUT = 'input[name^="nested_product_option"]';

    const cache = new Map(); // path -> html
    const inflight = new Map(); // path -> Promise<html>
    const scrollPositions = new Map(); // history entry id -> scrollY
    let latest = 0; // only the most recent navigation may land
    let prefetches = 0;
    let alignRun = 0; // only the newest swap may correct the scroll position
    let armed = false;
    let currentEntry = null;
    let currentPath = location.pathname;
    let liveRegion = null;
    let serverBodyClasses = Array.from(document.body.classList); // before any runtime class lands
    let wair = null; // WAIR size advisor instance, only reachable through its load event

    window.addEventListener('onPredictLoad', (event) => {
      wair = event.detail && event.detail.predict;
    });

    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');
    const toPath = (url) => {
      const target = new URL(url, location.href);
      return target.pathname + target.search;
    };
    const isProductPath = (path) => /\/products\/[^/?#]+/.test(path);
    const newEntryId = () => Date.now().toString(36) + Math.random().toString(36).slice(2, 6);

    function enabled() {
      if (window.Shopify && window.Shopify.designMode) return false;
      const param = new URLSearchParams(location.search).get(FLAG);
      try {
        if (param === '1' || param === '0') sessionStorage.setItem(FLAG, param);
        const sticky = sessionStorage.getItem(FLAG);
        if (sticky === '1') return true;
        if (sticky === '0') return false;
      } catch (error) {
        if (param === '1') return true;
        if (param === '0') return false;
      }
      return document.documentElement.classList.contains('pdp-swap') || window.PDP_SWAP === true;
    }

    const isModifiedClick = (event) =>
      event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey;

    // `quietUntil` tells the media gallery to skip its scroll-to-image while a swap settles,
    // otherwise its smooth scroll fights the swatch row staying put.
    const api = { enabled, navigate, isModifiedClick, SWATCH_INPUT, quietUntil: 0 };

    function remember(path, html) {
      cache.delete(path);
      cache.set(path, html);
      if (cache.size > CACHE_MAX) cache.delete(cache.keys().next().value);
    }

    function fetchPage(path) {
      if (cache.has(path)) return Promise.resolve(cache.get(path));
      if (inflight.has(path)) return inflight.get(path);
      const request = fetch(path, { credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error(`pdp-swap: ${response.status} for ${path}`);
          return response.text();
        })
        .then((html) => {
          remember(path, html);
          return html;
        })
        .finally(() => inflight.delete(path));
      inflight.set(path, request);
      return request;
    }

    function prefetch(path) {
      if (prefetches >= PREFETCH_MAX || cache.has(path) || inflight.has(path)) return;
      const connection = navigator.connection;
      if (connection && (connection.saveData || /2g/.test(connection.effectiveType || ''))) return;
      prefetches += 1;
      fetchPage(path).catch(() => {
        prefetches -= 1; // a failed prefetch saved nothing, so it does not use up the budget
      });
    }

    function readJson(doc, pattern) {
      for (const script of doc.querySelectorAll('script:not([src])')) {
        const match = script.textContent.match(pattern);
        if (match) {
          try {
            return JSON.parse(match[1]);
          } catch (error) {
            return null;
          }
        }
      }
      return null;
    }

    // Shopify's content_for_header: `var meta = {...};\n for (var attr in meta) {`
    function updateMeta(doc) {
      const meta = readJson(doc, /var meta = (\{[\s\S]*?\});\s*for \(var attr in meta\)/);
      if (!meta) return null;
      window.meta = meta;
      if (window.ShopifyAnalytics && window.ShopifyAnalytics.meta) Object.assign(window.ShopifyAnalytics.meta, meta);
      return meta;
    }

    function syncHead(doc) {
      document.title = doc.title;
      document.head.querySelectorAll(HEAD_TAGS).forEach((node) => node.remove());
      doc.head.querySelectorAll(HEAD_TAGS).forEach((node) => document.head.appendChild(document.adoptNode(node)));
    }

    // Only the classes a page renders server-side are ours to move: the template suffix differs
    // per product, so it has to come off again. Classes added at runtime by the theme or an app
    // (page-loaded, smart-cart--enabled, an A/B tool's hook) are left alone.
    function syncBodyClass(doc) {
      const next = Array.from(doc.body.classList);
      serverBodyClasses.forEach((cls) => {
        if (!next.includes(cls)) document.body.classList.remove(cls);
      });
      document.body.classList.add(...next);
      serverBodyClasses = next;
    }

    // App bundles and theme scripts must not re-execute per click.
    function dropLoadedScripts(node) {
      const loaded = new Set(Array.from(document.scripts, (script) => script.src).filter(Boolean));
      node.querySelectorAll('script[src]').forEach((script) => {
        // Resolved against this document, not the parsed one, so a relative src still matches.
        const src = isolated(() => new URL(script.getAttribute('src'), document.baseURI).href);
        if (src && loaded.has(src)) script.remove();
      });
    }

    // Sections below the product (recommendations, reviews) fill in after the swap, so the new
    // page is briefly shorter and the browser clamps the scroll position to the top. Holding the
    // old height keeps the visitor where they were.
    function holdHeight() {
      const main = document.getElementById('MainContent');
      const height = main ? main.offsetHeight : 0;
      return () => {
        const swapped = document.getElementById('MainContent');
        if (!swapped || !height) return;
        swapped.style.minHeight = `${height}px`;
        setTimeout(() => swapped.style.removeProperty('min-height'), HEIGHT_HOLD);
      };
    }

    // Reviews (Yotpo) and recommendations arrive empty in the new markup and fill in after
    // their app re-renders, so the page collapsed and re-grew around them. Each one keeps the
    // height its counterpart had until its own content has grown back (see holdUntilGrown).
    function holdWidgets() {
      const keyOf = (node) => {
        if (node.dataset.yotpoInstanceId) return `yotpo:${node.dataset.yotpoInstanceId}`;
        const owner = node.closest('.product__block, .shopify-section');
        return `recs:${owner ? owner.id || owner.className : ''}`;
      };
      const collect = () => {
        const groups = new Map();
        document.querySelectorAll(`#MainContent :is(${HELD_WIDGETS})`).forEach((node) => {
          const key = keyOf(node);
          if (!groups.has(key)) groups.set(key, []);
          groups.get(key).push(node);
        });
        return groups;
      };
      // Hold the block the widget sits in, not the widget: Yotpo puts a margin on its inner
      // element that collapses out of the widget box, so holding the widget alone still let the
      // title and gallery below move by that margin when the stars rendered.
      // `.product__block` is a flex item, so it contains that margin; the app-block wrapper does not.
      // <product-recommendations> is display: inline, where min-height does nothing, so the
      // related-products one is held on its section.
      const holderOf = (node) =>
        node.closest('.product__block') || node.closest('.shopify-app-block') || node.closest('.shopify-section') || node;
      const heights = new Map();
      collect().forEach((nodes, key) => heights.set(key, nodes.map((node) => holderOf(node).offsetHeight)));

      return () => {
        collect().forEach((nodes, key) => {
          nodes.forEach((node, index) => {
            const height = (heights.get(key) || [])[index];
            if (height) holdUntilGrown(holderOf(node), height);
          });
        });
      };
    }

    // On desktop the info column is sticky, and whether it can slide depends on how tall it is
    // next to the gallery. While the new product's widgets are still empty the column is briefly
    // shorter, sticks lower, then drops back as they fill — which read as the column jumping.
    // It keeps the old column's height until its own content has grown back into it.
    function holdInfoColumn() {
      const column = document.querySelector(`#MainContent ${INFO_COLUMN}`);
      const height = column ? column.offsetHeight : 0;
      return () => {
        const next = document.querySelector(`#MainContent ${INFO_COLUMN}`);
        if (next && height) holdUntilGrown(next, height);
      };
    }

    // Keeps `holder` at least `height` tall until its own content has grown back into that space.
    // A pause in DOM changes is not enough on its own: the complementary products arrive in two
    // requests (Shopify, then the Rebuy fallback) and the block is empty in the gap between them.
    // A block that is genuinely shorter for the new product is let go after HOLD_QUIET.
    function holdUntilGrown(holder, height) {
      holder.style.minHeight = `${height}px`;
      // How tall the content is regardless of the held min-height.
      const extent = () => {
        const top = holder.getBoundingClientRect().top;
        const bottom = Math.max(top, ...Array.from(holder.children, (child) => child.getBoundingClientRect().bottom));
        return bottom - top + parseFloat(getComputedStyle(holder).paddingBottom || 0);
      };
      let settle = 0;
      let quiet = 0;
      const release = () => {
        observer.disconnect();
        clearTimeout(settle);
        clearTimeout(quiet);
        clearTimeout(limit);
        holder.style.removeProperty('min-height');
      };
      const observer = new MutationObserver(() => {
        clearTimeout(settle);
        clearTimeout(quiet);
        settle = setTimeout(() => extent() >= height - 2 && release(), HOLD_SETTLE);
        quiet = setTimeout(release, HOLD_QUIET);
      });
      observer.observe(holder, { childList: true, subtree: true });
      const limit = setTimeout(release, HOLD_MAX);
    }

    // First match under #MainContent that is laid out: with the TW-209 rows the page also holds
    // a hidden copy of the colour swatches.
    function shown(selector) {
      return Array.from(document.querySelectorAll(`#MainContent ${selector}`)).find((node) => node.getClientRects().length) || null;
    }

    // Keeps the control that was clicked under the same point of the viewport, so the swatches
    // stay put instead of the page appearing to jump. Re-aligned for a moment afterwards
    // because star ratings and badges above them arrive late and push the row around.
    function keepPlace(selector) {
      // The control itself can be a visually hidden radio, so measure the block it sits in.
      const block = (node) => node && (node.closest('.product-form__input, .product__block') || node);
      const before = block(selector && shown(selector));
      // Inside a sticky column the viewport position follows the sticky offset, not the page
      // scroll, so scrolling to compensate only made the column see-saw.
      const sticky = before && before.closest('.sticky-element');
      const pinned = sticky && getComputedStyle(sticky).position === 'sticky';
      const top = before && !pinned ? before.getBoundingClientRect().top : null;
      return () => {
        if (top === null) return;
        // A second swatch click starts its own run; the older one's pending timers stop here
        // rather than pulling the page back towards where the first swatch used to be.
        const run = ++alignRun;
        const events = ['wheel', 'touchstart', 'keydown'];
        const release = () => {
          if (run === alignRun) alignRun += 1;
          events.forEach((type) => window.removeEventListener(type, release));
        };
        events.forEach((type) => window.addEventListener(type, release, { once: true, passive: true }));

        const align = () => {
          const after = run === alignRun && block(shown(selector));
          if (!after) return;
          const shift = after.getBoundingClientRect().top - top;
          if (Math.abs(shift) > 1) window.scrollBy({ top: shift, behavior: 'instant' });
        };
        align();
        ALIGN_RETRIES.forEach((delay) => setTimeout(align, delay));
        setTimeout(release, ALIGN_RETRIES[ALIGN_RETRIES.length - 1] + 100);
      };
    }

    // The incoming markup starts at the dimmed opacity the outgoing page is already showing and
    // eases up to full, so the swap is one continuous fade rather than dim-then-snap. Opacity on
    // two wrappers only — cheap enough to stay smooth on a phone.
    const FADE_TARGETS = '#MainContent .top-mobile-pdp, #MainContent .product';

    function fadeIn() {
      const nodes = document.querySelectorAll(FADE_TARGETS);
      nodes.forEach((node) => node.classList.add('pdp-swap-enter'));
      nodes.forEach((node) => node.getBoundingClientRect()); // flush styles: the transition needs a start value
      nodes.forEach((node) => node.classList.remove('pdp-swap-enter'));
    }

    // Entrance animations replay on inserted markup (the page fades in from white,
    // which reads as a reload). `hold` is the theme's own opt-out.
    function holdMotion(node) {
      node.querySelectorAll('motion-element').forEach((element) => element.setAttribute('hold', ''));
    }

    // Decode the first gallery images before the swap so the switch lands with pictures in
    // place. Same srcset/sizes as the markup, so the browser fetches the candidate it will use.
    function preloadImages(doc) {
      const sources = Array.from(doc.querySelectorAll('#MainContent .product__media-item img')).slice(
        0,
        PRELOAD_IMAGES
      );
      const decoded = sources.map((source) => {
        const image = new Image();
        if (source.getAttribute('sizes')) image.sizes = source.getAttribute('sizes');
        if (source.getAttribute('srcset')) image.srcset = source.getAttribute('srcset');
        image.src = source.getAttribute('src') || '';
        return image.decode().catch(() => {});
      });
      return Promise.race([Promise.all(decoded), new Promise((resolve) => setTimeout(resolve, PRELOAD_TIMEOUT))]);
    }

    function prepareNode(node) {
      dropLoadedScripts(node);
      holdMotion(node);
    }

    function swapNode(id, doc) {
      const oldNode = document.getElementById(id);
      const newNode = doc.getElementById(id);
      if (oldNode && newNode) {
        HTMLUpdateUtility.viewTransition(oldNode, newNode, [prepareNode], []);
      } else if (oldNode) {
        oldNode.remove();
      } else if (newNode) {
        const wrapper = document.createElement('div');
        prepareNode(newNode);
        HTMLUpdateUtility.setInnerHTML(wrapper, newNode.outerHTML);
        document.body.appendChild(wrapper.firstChild);
      }
    }

    function setLoading(on) {
      document.documentElement.classList.toggle('pdp-swapping', on);
      const main = document.getElementById('MainContent');
      if (!main) return;
      if (on) main.setAttribute('aria-busy', 'true');
      else main.removeAttribute('aria-busy');
    }

    function announce(text) {
      if (!liveRegion) {
        liveRegion = document.createElement('div');
        liveRegion.id = 'pdp-swap-status';
        liveRegion.className = 'visually-hidden';
        liveRegion.setAttribute('role', 'status');
        liveRegion.setAttribute('aria-live', 'polite');
        document.body.appendChild(liveRegion);
      }
      liveRegion.textContent = '';
      requestAnimationFrame(() => {
        liveRegion.textContent = text;
      });
    }

    // Option picks (Fit, Waist, Length…) carry over to the next colour when that product has
    // the same values in an available combination; anything that does not fit is left unselected.
    function readSelections() {
      const info = document.querySelector('#MainContent product-info');
      const data = info && info.productOptions;
      if (!data || !data.options) return [];
      return data.options
        .map((option) => {
          const field = info.querySelector(`[data-wair-option-name="${CSS.escape(option.name)}"]`);
          const picked = field && field.querySelector('input[type="radio"]:checked, select');
          return { name: option.name, value: (picked && picked.value) || null };
        })
        .filter((selection) => selection.value);
    }

    // Marks the carried-over picks in the incoming markup itself, before it is inserted, so the
    // pills render already selected. Clicking them after insertion let their 200 ms colour
    // transition run from white, which read as the size dropping out for a moment.
    function preselect(doc, saved) {
      const info = doc.querySelector('#MainContent product-info');
      const source = info && info.querySelector('[data-product-options]');
      if (!saved.length || !source) return false;
      let data;
      try {
        data = JSON.parse(source.textContent).product;
      } catch (error) {
        return false;
      }
      if (!data || !data.options || !data.variants) return false;

      const chosen = {}; // position -> value
      data.options.forEach((option) => {
        const wanted = saved.find((selection) => selection.name === option.name);
        if (!wanted) return;
        const fits = data.variants.some(
          (variant) =>
            variant.available &&
            variant[`option${option.position}`] === wanted.value &&
            Object.keys(chosen).every((position) => variant[`option${position}`] === chosen[position])
        );
        if (fits) chosen[option.position] = wanted.value;
      });

      let applied = false;
      data.options.forEach((option) => {
        const value = chosen[option.position];
        const field = value && info.querySelector(`[data-wair-option-name="${CSS.escape(option.name)}"]`);
        if (!field) return;
        const radios = field.querySelectorAll('input[type="radio"]');
        const radio = Array.from(radios).find((input) => input.value === value);
        const select = field.querySelector('select');
        if (radio) {
          // The class too, not just the attribute: <product-info> connects before its
          // <variant-selects> child and lays the page out in between, so a pill still waiting
          // for variant-selects to add `.checked` got styled white first and then animated.
          radios.forEach((input) => {
            input.toggleAttribute('checked', input === radio);
            input.classList.toggle('checked', input === radio);
          });
          applied = true;
        } else if (select) {
          select.querySelectorAll('option').forEach((item) => item.toggleAttribute('selected', item.value === value));
          applied = true;
        }
      });
      return applied;
    }

    // With the picks already in the markup, run the theme's own update (availability, variant id,
    // price, media, Add to cart) — the same path a click on an option takes.
    function applyPreselection() {
      const info = document.querySelector('#MainContent product-info');
      if (!info || typeof info.renderProductInfoDynamic !== 'function') return;
      info.hasUserInteracted = true;
      info.renderProductInfoDynamic();
    }

    function focusControl(selector) {
      const control = selector && shown(selector);
      if (control) control.focus({ preventScroll: true });
    }

    function renderWair(doc) {
      if (!wair) return;
      const product = readJson(
        doc,
        /var predictProduct = (\{[\s\S]*?\});\s*window\.addEventListener\("onPredictLoad"/
      );
      if (!product) return;
      wair.render({
        brandKey: window.Shopify.shop,
        product: { key: product.handle, name: product.title, image: product.featured_image },
      });
    }

    // Spurit Pre-Order keys its product data by handle, seeded from inline app embeds: one for
    // the product being viewed and one per product in the cart. Re-run all of them for the
    // incoming page — a handle it cannot find makes it throw on every later DOM mutation.
    function seedPreorder(doc) {
      if (!window.Spurit || !window.Spurit.Preorder2) return;
      const marker = 'Spurit.Preorder2.snippet.products[';
      doc.querySelectorAll('script:not([src])').forEach((script) => {
        const source = script.textContent;
        if (!source.includes(marker)) return;
        // Matched shape is exactly `products['handle'] = { … };`, and the object is not JSON
        // (unquoted keys), so it is run rather than parsed. Anchored per line and size-capped so
        // only that one statement can ever be executed.
        const assignments = source.match(SEED_ASSIGNMENT) || [];
        assignments
          .filter((assignment) => assignment.length <= SEED_MAX_LENGTH)
          .forEach((assignment) => isolated(() => new Function(assignment)()));
      });
    }

    // Each call isolated: an app throwing must never roll the swap back into a reload.
    function isolated(step) {
      const skipped = (error) => console.debug('[pdp-swap] app hook skipped', error);
      try {
        const result = step();
        if (result && typeof result.catch === 'function') result.catch(skipped);
        return result;
      } catch (error) {
        skipped(error);
        return undefined;
      }
    }

    function reinitApps(doc) {
      const handle = window.meta && window.meta.product && window.meta.product.handle;
      [
        () => window.Shopify?.PaymentButton?.init?.(),
        () => window.ProductModel?.loadShopifyXR?.(),
        () => window.yotpoWidgetsContainer?.initWidgets?.(),
        () => window.yotpo?.refreshWidgets?.(),
        () => handle && window._RestockRocket?.updateProductContext?.(handle), // back-in-stock buttons
        () => renderWair(doc),
      ].forEach(isolated);
    }

    function arm() {
      if (armed) return;
      armed = true;
      history.scrollRestoration = 'manual';
      window.addEventListener('scroll', () => scrollPositions.set(currentEntry, window.scrollY), { passive: true });
    }

    // Marks the entry the visitor landed on as ours and caches its page so Back
    // can restore it without a reload.
    function armLanding() {
      arm();
      if (currentEntry) return;
      const state = history.state || {};
      currentEntry = (state.pdpSwap && state.pdpSwap.id) || newEntryId();
      history.replaceState({ ...state, pdpSwap: { id: currentEntry } }, '');
      fetchPage(toPath(location.href)).catch(() => {});
    }

    function pushEntry(path) {
      scrollPositions.set(currentEntry, window.scrollY);
      currentEntry = newEntryId();
      history.pushState({ pdpSwap: { id: currentEntry } }, '', path);
    }

    // Throws if the response is not a usable product page; the caller then falls back to a load.
    function render(doc, path, { push, focus, scrollTop, historyPath }) {
      const main = doc.getElementById('MainContent');
      if (!main || !main.querySelector('product-info')) throw new Error('pdp-swap: response is not a product page');
      const ogTitle = doc.querySelector('meta[property="og:title"]');
      const title = (ogTitle && ogTitle.content) || doc.title;

      const commit = () => {
        api.quietUntil = Date.now() + QUIET_WINDOW;
        const preselected = isolated(() => preselect(doc, readSelections()));
        const restoreWidgets = isolated(holdWidgets) || (() => {});
        const restoreColumn = isolated(holdInfoColumn) || (() => {});
        const restoreHeight = holdHeight();
        const restorePlace = keepPlace(focus);
        // Before the DOM swap: app observers fire the moment the markup changes.
        updateMeta(doc);
        isolated(() => seedPreorder(doc));
        swapNode('MainContent', doc);
        swapNode('shopify-section-sticky-atc-bar', doc);
        if (preselected) isolated(applyPreselection);
        isolated(restoreWidgets);
        isolated(restoreColumn);
        syncHead(doc);
        syncBodyClass(doc);
        if (push) pushEntry(historyPath || path);
        currentPath = location.pathname;
        restoreHeight();
        if (scrollTop !== undefined) window.scrollTo({ top: scrollTop, behavior: 'instant' });
        else isolated(restorePlace);
        fadeIn();
        setLoading(false);

        reinitApps(doc);
        announce(title);
        focusControl(focus);
        document.dispatchEvent(new CustomEvent('pdp:swapped', { detail: { url: path, product: window.meta && window.meta.product } }));
      };

      commit();
      return Promise.resolve();
    }

    function navigate(url, { focus } = {}) {
      const target = new URL(url, location.href);
      if (target.pathname === location.pathname) return;
      const path = target.pathname + target.search;
      if (!isProductPath(path)) {
        window.location.href = url;
        return;
      }
      const historyPath = path + target.hash; // the fragment is not part of the cache key
      armLanding();
      const token = ++latest;
      setLoading(true);
      fetchPage(path)
        .then((html) => {
          if (token !== latest) return;
          const doc = parse(html);
          return preloadImages(doc).then(() => {
            if (token !== latest) return;
            return render(doc, path, { push: true, focus, historyPath });
          });
        })
        .catch((error) => {
          if (token !== latest) return;
          cache.delete(path);
          setLoading(false);
          console.warn('[pdp-swap] falling back to a full page load', error);
          window.location.href = url;
        });
    }

    window.addEventListener('popstate', (event) => {
      const state = event.state && event.state.pdpSwap;
      const path = location.pathname;
      if (!state && !(armed && isProductPath(path))) return; // not ours: hash links, other scripts
      if (path === currentPath) return;
      arm();
      scrollPositions.set(currentEntry, window.scrollY); // manual restoration: still the entry we are leaving
      currentEntry = (state && state.id) || newEntryId();
      const token = ++latest;
      setLoading(true);
      fetchPage(path + location.search)
        .then((html) => {
          if (token !== latest) return;
          const doc = parse(html);
          return preloadImages(doc).then(() => {
            if (token !== latest) return;
            return render(doc, path + location.search, {
              push: false,
              scrollTop: scrollPositions.get(currentEntry) || 0,
            });
          });
        })
        .catch(() => {
          if (token !== latest) return;
          setLoading(false);
          window.location.reload();
        });
    });

    // Entry point 2: Style tabs (style_options block). Modified clicks keep the native link.
    document.addEventListener('click', (event) => {
      const link = event.target.closest(STYLE_TAB);
      if (!link || isModifiedClick(event) || !enabled()) return;
      event.preventDefault();
      navigate(link.href, { focus: `${STYLE_TAB}.is-active` });
    });

    // Prefetch on hover / keyboard focus, delayed so a pass-over costs nothing.
    let hoverTarget = null;
    let hoverTimer = 0;

    function prefetchUrlFor(element) {
      if (!element.closest || element.closest('.quick-view__content')) return null;
      const link = element.closest(STYLE_TAB);
      if (link) return { target: link, url: link.getAttribute('href') };
      let input = element.closest(SWATCH_INPUT);
      const label = input ? null : element.closest('label[for]');
      if (label) input = document.getElementById(label.getAttribute('for'));
      if (!input || !input.name || !input.name.startsWith('nested_product_option')) return null;
      return { target: label || input, url: input.dataset.productUrl };
    }

    function cancelPrefetch() {
      clearTimeout(hoverTimer);
      hoverTarget = null;
    }

    function schedulePrefetch(element) {
      const found = prefetchUrlFor(element);
      if (!found || found.target === hoverTarget) return;
      cancelPrefetch();
      if (!found.url || !isProductPath(found.url) || !enabled()) return;
      hoverTarget = found.target;
      hoverTimer = setTimeout(() => prefetch(toPath(found.url)), PREFETCH_DELAY);
    }

    document.addEventListener('mouseover', (event) => schedulePrefetch(event.target));
    document.addEventListener('mouseout', (event) => {
      if (hoverTarget && !hoverTarget.contains(event.relatedTarget)) cancelPrefetch();
    });
    document.addEventListener('focusin', (event) => schedulePrefetch(event.target));
    document.addEventListener('focusout', cancelPrefetch);

    // Persists ?pdp_swap=… for the session before the first swap drops it from the URL, and puts
    // the flag class on <html> in that case too, so CSS keyed on it (the badge row) matches.
    if (enabled()) document.documentElement.classList.add('pdp-swap');

    return api;
  })();

if (!customElements.get("variant-selects")) {
  customElements.define(
    "variant-selects",
    class VariantSelects extends HTMLElement {
      constructor() {
        super();
      }

      async switchNestedProduct(url) {
        const container = this.closest(".quick-view__content")
        if (!container) return;

        container.style.opacity = "0.5";
        container.style.pointerEvents = "none";

        try {
          const response = await fetch(url);
          const html = await response.text();
          const parser = new DOMParser();
          const doc = parser.parseFromString(html, "text/html");

          const newContent = doc.querySelector(".quick-view__content")
          console.log("New content loaded:", newContent);

          if (newContent) {
            container.innerHTML = newContent.innerHTML;
            this.reinit(container)
          }
        } catch (error) {
          console.error("Failed to load product:", error);
        } finally {
          container.style.opacity = "1";
          container.style.pointerEvents = "all";
        }
      }

      reinit(container) {
        if (window.FoxTheme && window.FoxTheme.init) {
          window.FoxTheme.init(container);
        }
      }

      get selectedOptionValues() {
        return Array.from(
          this.querySelectorAll(
            "select option[selected], fieldset input:checked",
          ),
        )
          .map((element) => {
            if (element.dataset.optionValueId) {
              return element.dataset.optionValueId;
            }
            if (element.dataset.productId) {
              return element.dataset.productId;
            }
            return element.value;
          })
          .filter((value) => value);
      }

      getInputForEventTarget(target) {
        return target.tagName === "SELECT" ? target.selectedOptions[0] : target;
      }

      connectedCallback() {
        this.querySelectorAll("input").length > 0 &&
          this.querySelectorAll("input").forEach((input) => {
            if (input.checked || input.hasAttribute("checked")) {
              input.checked = true;
              input.classList.add("checked");
            }
            input.addEventListener("click", (event) => {
              if (input.classList.contains("checked")) {
                input.checked = false;
                input.classList.remove("checked");
              } else {
                input.parentElement
                  .querySelector("input.checked")
                  ?.classList.remove("checked");
                input.classList.add("checked");
              }
              this.changeHandler(event);
            });
          });

        this.addEventListener("change", (event) => this.changeHandler(event));

        this.addEventListener("click", (event) => {
          this.handleNestedProductClick(event);
        });
      }

      changeHandler(event) {
        const target = this.getInputForEventTarget(event.target);
        this.updateSelectedSwatchValue(event);

        FoxTheme.pubsub.publish(
          FoxTheme.pubsub.PUB_SUB_EVENTS.optionValueSelectionChange,
          {
            data: {
              event,
              target,
              selectedOptionValue: target.value,
            },
          },
        );
      }

      updateSelectedSwatchValue({ target }) {
        const { value, tagName } = target;

        if (tagName === "SELECT" && target.selectedOptions.length) {
          Array.from(target.options)
            .find((option) => option.getAttribute("selected"))
            .removeAttribute("selected");
          target.selectedOptions[0].setAttribute("selected", "selected");

          const swatchValue =
            target.selectedOptions[0].dataset.optionSwatchValue;
          const selectedDropdownSwatchValue = target
            .closest(".product-form__input")
            .querySelector("[data-selected-value] > .swatch");
          if (!selectedDropdownSwatchValue) return;
          if (swatchValue) {
            selectedDropdownSwatchValue.style.setProperty(
              "--swatch--background",
              swatchValue,
            );
            selectedDropdownSwatchValue.classList.remove("swatch--unavailable");
          } else {
            selectedDropdownSwatchValue.style.setProperty(
              "--swatch--background",
              "unset",
            );
            selectedDropdownSwatchValue.classList.add("swatch--unavailable");
          }

          selectedDropdownSwatchValue.style.setProperty(
            "--swatch-focal-point",
            target.selectedOptions[0].dataset.optionSwatchFocalPoint || "unset",
          );
        } else if (tagName === "INPUT" && target.type === "radio") {
          const selectedSwatchValue = target
            .closest(`.product-form__input`)
            .querySelector("[data-selected-value]");
          if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
        }
      }

      handleNestedProductClick(event) {
        if (event.target.closest(".quick-view__content")) {
          const label = event.target.closest("label");
          if (!label) return;
  
          const input = document.getElementById(label.getAttribute("for"));
  
          if (
            input &&
            input.name.startsWith("nested_product_option") &&
            input.dataset.productUrl
          ) {
            event.preventDefault(); 
            const url = input.dataset.productUrl;
            this.switchNestedProduct(url);
          }
        } else {
          const label = event.target.closest('label');
          // Keyboard (Space / arrow keys) clicks the radio itself, not its label
          const input = label
            ? document.getElementById(label.getAttribute('for') || '')
            : event.target.closest('input[type="radio"]');
          if (!input || !input.name.startsWith('nested_product_option') || !input.dataset.productUrl) return;

          // TW-218: in-place swap (variant B). Control below stays exactly as before.
          if (window.PdpSwap && PdpSwap.enabled() && !PdpSwap.isModifiedClick(event)) {
            if (label) event.preventDefault(); // no synthetic input click, so this runs once
            input.checked = true;
            // TW-209: rows are separate radio groups, so clear the old pick in the others.
            this.querySelectorAll(`${PdpSwap.SWATCH_INPUT}:checked`).forEach((other) => {
              if (other !== input) other.checked = false;
            });
            PdpSwap.navigate(input.dataset.productUrl, { focus: `${PdpSwap.SWATCH_INPUT}:checked` });
            return;
          }
          if (!label) return;

          // Navigate to product without URL parameters
          window.location.href = input.dataset.productUrl;
        }
      }
    },
  );
}
